import { GridApi } from 'ag-grid-community'
import { Dependency } from './DependencyContext'
import { WbsItemInfo } from '../types'
import objects from '../../../../../../../../../../utils/objects'

type CascadeDirection = 'forward' | 'backward'

// カスケードを打ち切るステータス（WbsItemStatus.DONE, WbsItemStatus.DISCARD）
const TERMINAL_STATUSES: ReadonlySet<string> = new Set(['DONE', 'DISCARD'])

export type DependentItem = {
  displayName: string
  isNotExpanded: boolean
}

type CollectDependentWbsItemNamesParams = {
  sourceWbsItemUuids: Set<string>
  directions: CascadeDirection[]
  dependencies: Dependency[]
  wbsItemUuidToRowIndexMap: ReadonlyMap<string, number>
  api: GridApi
  dataPath: string
  wbsItemInfoMap: ReadonlyMap<string, WbsItemInfo>
}

/**
 * 依存関係のあるWBSアイテムの表示名をBFS走査で収集する。
 * cascadeDependencySlide.ts の collectCascadedRows() と同じBFSロジックだが、
 * 日付シフトを行わず名前だけを収集する軽量版。
 *
 * 打ち切り条件は collectCascadedRows と同一:
 * - 行データ未取得またはrowIndexが古い（wbsItem.uuid不一致）
 * - Done/DiscardステータスのWBSアイテム
 *
 * ただし行データ未取得の場合でも、wbsItemInfoMapから名前を取得してツールチップに表示する。
 */
export const collectDependentWbsItemNames = ({
  sourceWbsItemUuids,
  directions,
  dependencies,
  wbsItemUuidToRowIndexMap,
  api,
  dataPath,
  wbsItemInfoMap,
}: CollectDependentWbsItemNamesParams): DependentItem[] => {
  const prefix = dataPath ? dataPath + '.' : ''
  const items: DependentItem[] = []
  const visited = new Set<string>(sourceWbsItemUuids)

  for (const direction of directions) {
    // 隣接リスト構築: カスケード方向に応じて辿る向きを変える
    const adjacency = new Map<string, string[]>()
    for (const dep of dependencies) {
      if (!dep.fromWbsItemUuid || !dep.toWbsItemUuid) continue
      const [source, target] =
        direction === 'forward'
          ? [dep.fromWbsItemUuid, dep.toWbsItemUuid]
          : [dep.toWbsItemUuid, dep.fromWbsItemUuid]
      const list = adjacency.get(source)
      if (list) {
        list.push(target)
      } else {
        adjacency.set(source, [target])
      }
    }

    // BFS: visitedのスナップショットからseedする
    // （Set.for...ofは反復中の追加要素も訪問するため、直接反復すると
    // identity チェックなしで全チェーンが展開されてしまう）
    const queue: string[] = []
    const seedUuids = [...visited]
    for (const uuid of seedUuids) {
      const neighbors = adjacency.get(uuid)
      if (neighbors) {
        for (const n of neighbors) {
          if (!visited.has(n)) {
            visited.add(n)
            queue.push(n)
          }
        }
      }
    }

    while (queue.length > 0) {
      const currentUuid = queue.shift()!

      // 行データ取得
      const rowIndex = wbsItemUuidToRowIndexMap.get(currentUuid)
      const node =
        rowIndex !== undefined
          ? api.getDisplayedRowAtIndex(rowIndex)
          : undefined
      const wbsItem = node?.data
        ? objects.getValue(node.data, `${prefix}wbsItem`)
        : undefined

      // 行データ未取得 or rowIndexが古い（折りたたみ等で別の行を指している）:
      // カスケードを打ち切り（後続/先行にも伝播しない）が、ツールチップには表示する
      if (!wbsItem || wbsItem.uuid !== currentUuid) {
        const displayName = wbsItemInfoMap.get(currentUuid)?.displayName
        if (displayName) {
          items.push({ displayName, isNotExpanded: true })
        }
        continue
      }

      // Done/Discard: カスケードを打ち切り（後続/先行にも伝播しない）
      if (wbsItem.status && TERMINAL_STATUSES.has(wbsItem.status)) {
        continue
      }

      // BFS走査を継続
      const neighbors = adjacency.get(currentUuid)
      if (neighbors) {
        for (const n of neighbors) {
          if (!visited.has(n)) {
            visited.add(n)
            queue.push(n)
          }
        }
      }

      // 表示名を取得: グリッド表示行 → wbsItemInfoMapフォールバック
      const displayName =
        wbsItem?.displayName ??
        wbsItemInfoMap.get(currentUuid)?.displayName ??
        undefined
      if (displayName) {
        items.push({ displayName, isNotExpanded: false })
      }
    }
  }

  return items
}
